import React, { useRef, useEffect } from "react";
import styles from "./support-chat-modal.module.scss";
import arrowBlueIcon from "@src/assets/svg/right-down-blue-arrow.svg";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { useSupportChatHook } from "./use-support-chat-hook";
import { ChatInput } from "./chat-input";
import { SupportChatTicketCard } from "./support-chat-ticket-card";
import { ChatMessage } from "./chat-message";
import Spinner from "@src/components/atoms/spinner/spinner";
import { SmallSpinner } from "@src/components/atoms/small-spinner/small-spinner";
import { useI18N } from "@src/language/language-context";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";

const cssClassName = "support-chat-modal";

export const SupportChatModal = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  /* const messagesEndRef = useRef<HTMLDivElement>(null); */
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const i18n = useI18N();

  const texts = {
    markAsDoneButton: i18n("modules.supportChatModal.markAsDoneButton"),
    humanSupportButton: i18n("modules.supportChatModal.humanSupportButton"),
    sseUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),
  };

  const {
    ticketInformation,
    comments,
    isLoading,
    input,
    setInput,
    isSending,
    sendMessage,
    refreshTicketAndComments,
    refreshTicketData,
    handleCloseModal,
    isWaitingForAIResponse,
    userId,
    namespace,
    updateMessageFeedback,
    showHumanSupportButton,
    showMarkAsDoneButton,
    patchTicketAssignee,
    patchTicketMarkAsDone,
    isPatchingAssignee,
    isPatchingStatus,
    isSSEUnavailable,
    streamingAiReplyText,
    streamingAiReplyTools,
  } = useSupportChatHook();

  const isAIRagLLMBot = ticketInformation?.handled_by_ai_chatbot === true;
  // Feedback changes `useful`, not the message, so it must not trigger auto-scroll.
  // Attachment IDs stay in the key because file events update existing comments.
  const commentsAutoScrollKey = JSON.stringify(
    comments.map(({ id, content, attachments }) => [
      id,
      content,
      attachments?.map(({ id: attachmentId }) => attachmentId) ?? [],
    ]),
  );

  useEffect(() => {
    if (!isLoading) {
      requestAnimationFrame(() => {
        chatContainerRef.current?.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    }
  }, [
    commentsAutoScrollKey,
    isLoading,
    isWaitingForAIResponse,
    showHumanSupportButton,
    streamingAiReplyText,
    streamingAiReplyTools,
  ]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__header`]}>
        <IconButton fun={() => handleCloseModal()} icon={arrowBlueIcon} />
        <span className={styles[`${cssClassName}__header-handle`]} />
        <div />
      </div>
      {ticketInformation && (
        <SupportChatTicketCard
          isBot={isAIRagLLMBot}
          isMachineSaveDisabled={isSending || isWaitingForAIResponse}
          ticket={ticketInformation}
          onAutoTranslationToggle={refreshTicketAndComments}
          onMachineAssignment={refreshTicketData}
        />
      )}

      <div
        ref={chatContainerRef}
        className={styles[`${cssClassName}__chat-container`]}
      >
        {isLoading ? (
          <div className={styles[`${cssClassName}__loader`]}>
            <Spinner />
          </div>
        ) : (
          <div className={styles[`${cssClassName}__messages-wrapper`]}>
            {comments.map((comment, index) => {
              const isLast = index === comments.length - 1;
              const isLastBotComment =
                isLast && comment.is_ai_chatbot_comment === true;

              return (
                <ChatMessage
                  key={comment.id}
                  comment={comment}
                  userId={userId}
                  namespace={namespace ?? null}
                  onFeedbackChange={updateMessageFeedback}
                  isThinking={isAIRagLLMBot && isWaitingForAIResponse && isLast}
                  tools={
                    isLastBotComment && streamingAiReplyTools.length > 0
                      ? streamingAiReplyTools
                      : undefined
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      <div className={styles[`${cssClassName}__buttons-wrapper`]}>
        {showMarkAsDoneButton && !isWaitingForAIResponse && !isSending && (
          <CustomTooltip
            content={isSSEUnavailable ? texts.sseUnavailableTooltip : ""}
            position="top"
          >
            <ButtonWithLoader
              variant={["outline"]}
              fun={() => {
                if (!isSSEUnavailable) {
                  patchTicketMarkAsDone(ticketInformation.id);
                }
              }}
              text={texts.markAsDoneButton}
              loading={isPatchingStatus}
              disabled={isSSEUnavailable}
            />
          </CustomTooltip>
        )}
        {showHumanSupportButton && !isWaitingForAIResponse && !isSending && (
          <CustomTooltip
            content={isSSEUnavailable ? texts.sseUnavailableTooltip : ""}
            position="top"
          >
            <ButtonWithLoader
              variant={"outline"}
              fun={() => {
                if (!isSSEUnavailable) {
                  patchTicketAssignee(ticketInformation.id);
                }
              }}
              text={texts.humanSupportButton}
              loading={isPatchingAssignee}
              disabled={isSSEUnavailable}
            />
          </CustomTooltip>
        )}
      </div>

      <ChatInput
        value={input}
        onChange={setInput}
        onSend={(token, type, fileName, attachmentFile, files) =>
          sendMessage(token, type, undefined, fileName, attachmentFile, files)
        }
        isSending={isSending || isWaitingForAIResponse}
        isDisabled={isSSEUnavailable}
        disabledTooltip={isSSEUnavailable ? texts.sseUnavailableTooltip : ""}
        inputRef={inputRef}
        ticketInformation={ticketInformation}
      />
    </div>
  );
};
